import { useMutation, useQuery } from '@tanstack/react-query';
import {
  GetAuditReportRequest,
  GetTimesheetExcelExportRequest,
  GetTimesheetReportRequest,
} from 'src/api/FiretrolApiClient';
import { timesheetApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';
import { getBrowserTimeZone } from 'src/utils';

export type IPayrollPdfRequest = Omit<GetTimesheetReportRequest, 'timeZone'>;
export type IAuditPdfRequest = Omit<GetAuditReportRequest, 'timeZone'>;

// A rendered report is a multi-megabyte blob that is only worth keeping while
// the report page shows it. The server renders PDFs one at a time in a single
// browser, so a failed render is not retried.
const PDF_QUERY_OPTIONS = { staleTime: 0, gcTime: 0, retry: false } as const;

/** @registry The Timesheet Audit Report PDF for one employee and date range, rendered by the server in the browser's time zone. */
export function useTimesheetAuditPdf(request: IAuditPdfRequest | null) {
  const fullRequest: GetAuditReportRequest | null = request
    ? { ...request, timeZone: getBrowserTimeZone() }
    : null;
  return useQuery({
    queryKey: queryKeys.timesheets.auditReport(fullRequest ?? {}),
    queryFn: ({ signal }) =>
      timesheetApiClient.getTimesheetAuditPdf(
        fullRequest!,
        toCancelToken(signal),
      ),
    enabled: !!fullRequest,
    ...PDF_QUERY_OPTIONS,
    meta: { errorContext: 'timesheet audit report pdf' },
  });
}

/** @registry The weekly Timesheet Payroll Report PDF for one office, rendered by the server in the browser's time zone. */
export function useTimesheetPayrollPdf(request: IPayrollPdfRequest | null) {
  const fullRequest: GetTimesheetReportRequest | null = request
    ? { ...request, timeZone: getBrowserTimeZone() }
    : null;
  return useQuery({
    queryKey: queryKeys.timesheets.payrollReport(fullRequest ?? {}),
    queryFn: ({ signal }) =>
      timesheetApiClient.getTimesheetPayrollPdf(
        fullRequest!,
        toCancelToken(signal),
      ),
    enabled: !!fullRequest,
    ...PDF_QUERY_OPTIONS,
    meta: { errorContext: 'timesheet payroll report pdf' },
  });
}

/** @registry Downloads the payroll report as an Excel workbook. */
export function useTimesheetExcelExport() {
  return useMutation({
    mutationFn: (request: GetTimesheetExcelExportRequest) =>
      timesheetApiClient.getTimesheetExcelExport(request),
    meta: { errorContext: 'timesheet excel export' },
  });
}
